import { useMemo, useState, type ReactNode } from "react";
import { AlertTriangle, Check, ChevronDown, Clock, Lightbulb } from "lucide-react";
import {
  MEETING_START_TIME_PATTERN,
  upcomingMeetingDates,
  zonedWallTimeToInstant,
} from "@spectree/shared";
import { Segmented } from "@/components/grooming/grooming-chips";
import { PROJECT_GHOST_ACTION, PROJECT_PRIMARY_BUTTON } from "@/components/grooming/project/project-styles";
import { TimeZonePicker } from "@/components/grooming/project/time-zone-picker";
import { ErrorLine } from "@/components/toolbox/error-line";
import { Button } from "@/components/ui/button";
import { useMe } from "@/hooks/queries/use-me";
import { useSetMeetingPattern, useTeamTimeZones } from "@/hooks/queries/use-grooming";
import { ApiError } from "@/lib/api/client";
import { getApiErrorCode } from "@/lib/api/work-items";
import type { GroomingConfig, GroomingMeetingPattern } from "@/lib/api/grooming";
import { dateParts, localToday } from "@/lib/grooming/agenda-run";
import { formatLocalClockParts } from "@/lib/grooming/local-time";
import { formatMeetingDate, localDateIn, prepareByDate } from "@/lib/grooming/meeting-dates";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { currentZoneId, detectedTimeZone, isKnownTimeZone, zoneCity, zoneLabel, zoneName } from "@/lib/time-zones";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * Project → Meeting schedule (epic PI-SPEC-SW-E294, feature PI-SPEC-SW-1511,
 * PKG-PM-F frame F1, UX-PM-15; 01-roles.md §2 and §9.6): the Project
 * Manager sets when the grooming meeting repeats without typing a zone id,
 * and sees what it means before saving.
 *
 * Left, "When does the grooming meeting repeat?": Day (Mon–Fri; a stored
 * Saturday or Sunday still shows selected), How often (every 1–4 weeks),
 * Starts at (15-minute steps, required), First meeting and Time zone (a
 * searchable list with the team's zones first). Save schedule sends all five
 * fields; Cancel goes back to the saved schedule; Stop repeating clears it. A
 * failed save stays on the page with its cause (`ErrorLine`).
 *
 * Right, "Next meetings": the next three meetings from the draft, updated on
 * every change before Save, each with its hour in the viewer's own zone
 * (their profile's, else this computer's) and when to aim to prepare (the
 * business day before, 01-roles.md §9.3: never forced). The arithmetic is
 * `@spectree/shared`'s, so the preview is the API's `nextMeetingStart`. A
 * pattern saved before start times existed shows dates only and asks for a
 * start time.
 *
 * It replaced the Session page's "When does the grooming meeting repeat?"
 * dialog: Session links here. `map-project` holders only (the Project
 * route's gate); the API refuses everyone else with 403.
 */
export interface MeetingScheduleTabProps {
  project: string;
  config: GroomingConfig | undefined;
  /** "Now", for the next meetings and the offsets. Default: the current time (tests pass a fixed one). */
  now?: Date | undefined;
  /** The viewer's IANA zone. Default: their profile's (`/me`), else this computer's. */
  viewerTimeZone?: string | undefined;
}

/** The schedule being edited. `startTime` is "" until one is picked (a pattern saved before PKG-PM-F has none). */
export interface MeetingScheduleDraft {
  weekday: number;
  everyNWeeks: number;
  anchorDate: string;
  timeZone: string;
  startTime: string;
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

const HOW_OFTEN = [
  { value: "1", label: "Every week" },
  { value: "2", label: "Every 2 weeks" },
  { value: "3", label: "3 weeks" },
  { value: "4", label: "4 weeks" },
] as const;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** `HH:mm` → "10:00 AM" (12-hour, the en-US way; ordinary spaces). */
export function startTimeLabel(time: string): string {
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "UTC" })
    .format(new Date(Date.UTC(1970, 0, 1, hours, minutes)))
    .replace(/[\u202f\u00a0]/g, " ");
}

/** Every 15 minutes of the day, `00:00` to `23:45`. */
export const START_TIME_OPTIONS: readonly string[] = Array.from({ length: 96 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
});

/** The draft a saved pattern opens as; with none, today's weekday (Monday at a weekend), every week, from today, this computer's zone, no start time. */
export function draftFromPattern(pattern: GroomingMeetingPattern | null, now: Date = new Date()): MeetingScheduleDraft {
  if (pattern) {
    return {
      weekday: pattern.weekday,
      everyNWeeks: pattern.everyNWeeks,
      anchorDate: pattern.anchorDate,
      timeZone: currentZoneId(pattern.timeZone),
      startTime: pattern.startTime ?? "",
    };
  }
  const today = localToday(now);
  const weekday = dateParts(today).getDay();
  return {
    weekday: weekday === 0 || weekday === 6 ? 1 : weekday,
    everyNWeeks: 1,
    anchorDate: today,
    timeZone: currentZoneId(detectedTimeZone()),
    startTime: "",
  };
}

/** True when Save may send the draft: a start time, a first-meeting date and a zone the browser knows. */
export function isDraftComplete(draft: MeetingScheduleDraft): boolean {
  return (
    MEETING_START_TIME_PATTERN.test(draft.startTime) && DATE_PATTERN.test(draft.anchorDate) && isKnownTimeZone(draft.timeZone)
  );
}

/**
 * Why a save failed, for the error line: a project not set up yet, the 403
 * and 400 causes in words, else that SpecTree did not answer.
 */
export function scheduleSaveCause(error: unknown): string {
  if (getApiErrorCode(error) === "GROOMING_CONFIG_NOT_FOUND") {
    return "This project is not set up for grooming yet. An Admin adds its repositories first (Repositories tab); then the schedule can be saved.";
  }
  if (error instanceof ApiError) {
    if (error.response.status === 403) return "Only Project Managers and Admins can change the schedule.";
    if (error.response.status === 400) return error.message;
  }
  return "SpecTree did not answer. Nothing changed.";
}

function sameDraft(a: MeetingScheduleDraft, b: MeetingScheduleDraft): boolean {
  return (
    a.weekday === b.weekday &&
    a.everyNWeeks === b.everyNWeeks &&
    a.anchorDate === b.anchorDate &&
    a.timeZone === b.timeZone &&
    a.startTime === b.startTime
  );
}

export function MeetingScheduleTab({ project, config, now, viewerTimeZone }: MeetingScheduleTabProps) {
  const pattern = config?.meetingPattern ?? null;
  // A new saved schedule (after Save, or someone else's) opens the form afresh.
  const formKey = JSON.stringify(pattern);
  return <MeetingScheduleForm key={formKey} project={project} pattern={pattern} now={now} viewerTimeZone={viewerTimeZone} />;
}

interface SaveFailure {
  title: string;
  cause: string;
  retry: GroomingMeetingPattern | null;
}

function MeetingScheduleForm({
  project,
  pattern,
  now,
  viewerTimeZone,
}: {
  project: string;
  pattern: GroomingMeetingPattern | null;
  now: Date | undefined;
  viewerTimeZone: string | undefined;
}) {
  const saved = useMemo(() => draftFromPattern(pattern, now), [pattern, now]);
  const [draft, setDraft] = useState<MeetingScheduleDraft>(saved);
  const [failure, setFailure] = useState<SaveFailure | null>(null);
  const save = useSetMeetingPattern(project);
  const team = useTeamTimeZones(project);
  const me = useMe();
  const teamZones = team.data ?? [];
  const detected = currentZoneId(detectedTimeZone());
  const viewerZone = [viewerTimeZone, me.data?.timeZone, detected].find(
    (zone): zone is string => typeof zone === "string" && isKnownTimeZone(zone)
  ) ?? "UTC";

  const change = (patch: Partial<MeetingScheduleDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const submit = (next: GroomingMeetingPattern | null) => {
    setFailure(null);
    save.mutate(next, {
      onSuccess: () => {
        showGroomingToast(next ? "Schedule saved" : "Meeting schedule removed");
      },
      onError: (cause) => {
        setFailure({
          title: next ? "The schedule was not saved." : "The schedule was not removed.",
          cause: scheduleSaveCause(cause),
          retry: next,
        });
      },
    });
  };

  const complete = isDraftComplete(draft);
  const dirty = !sameDraft(draft, saved);
  const legacy = pattern !== null && !pattern.startTime;
  const days = [1, 2, 3, 4, 5];
  if (!days.includes(draft.weekday)) days.push(draft.weekday);
  const startOptions = START_TIME_OPTIONS.includes(draft.startTime) || !draft.startTime
    ? START_TIME_OPTIONS
    : [...START_TIME_OPTIONS, draft.startTime].sort();

  return (
    <div className="flex flex-col gap-3.5" data-testid="project-meeting-schedule">
      {failure && (
        <ErrorLine
          title={failure.title}
          cause={failure.cause}
          onRetry={() => {
            submit(failure.retry);
          }}
          onClose={() => {
            setFailure(null);
          }}
        />
      )}
      <div className="flex flex-wrap items-stretch gap-4">
        <section
          className="glass-card flex min-w-[420px] flex-1 flex-col gap-4 rounded-[10px] px-5 py-[18px]"
          aria-labelledby="meeting-schedule-heading"
          data-testid="meeting-schedule-form"
        >
          <div className="flex flex-col gap-1">
            <h2 id="meeting-schedule-heading" className="font-display text-base font-semibold">
              When does the grooming meeting repeat?
            </h2>
            <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")}>
              The Session screen takes its date from this. One session can still be moved on its own with Change date.
            </p>
          </div>

          <FormRow label="Day">
            <Segmented<string>
              label="Day"
              value={String(draft.weekday)}
              options={days.map((day) => ({ value: String(day), label: WEEKDAY_SHORT[day] ?? String(day) }))}
              onChange={(value) => {
                change({ weekday: Number(value) });
              }}
            />
          </FormRow>

          <FormRow label="How often">
            <Segmented<string>
              label="How often"
              value={String(draft.everyNWeeks)}
              options={HOW_OFTEN}
              onChange={(value) => {
                change({ everyNWeeks: Number(value) });
              }}
            />
          </FormRow>

          <FormRow label="Starts at" htmlFor="meeting-start-time">
            <span className="flex items-center gap-3">
              <span className="relative inline-flex w-[132px]">
                <Clock
                  className={cn("pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2", TOOLBOX_TEXT.faint)}
                  aria-hidden="true"
                />
                <select
                  id="meeting-start-time"
                  aria-label="Starts at"
                  required
                  className={cn(
                    "h-8 w-full appearance-none rounded-lg border border-[#3F3F46] bg-[#111113] pl-8 pr-7 text-[#FAFAFA]",
                    "focus-visible:border-[#23A4FF] focus-visible:outline-none",
                    TOOLBOX_TEXT.content
                  )}
                  value={draft.startTime}
                  onChange={(event) => {
                    change({ startTime: event.target.value });
                  }}
                >
                  <option value="" disabled>
                    Pick a time
                  </option>
                  {startOptions.map((time) => (
                    <option key={time} value={time}>
                      {startTimeLabel(time)}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className={cn("pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2", TOOLBOX_TEXT.faint)}
                  aria-hidden="true"
                />
              </span>
              {legacy && !draft.startTime && <span className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")}>New</span>}
            </span>
          </FormRow>

          <FormRow label="First meeting" htmlFor="meeting-first-date">
            <input
              id="meeting-first-date"
              type="date"
              aria-label="First meeting"
              className={cn(
                "h-8 w-[220px] rounded-lg border border-[#3F3F46] bg-[#111113] px-2.5 text-[#FAFAFA] [color-scheme:dark]",
                "focus-visible:border-[#23A4FF] focus-visible:outline-none",
                TOOLBOX_TEXT.content
              )}
              value={draft.anchorDate}
              onChange={(event) => {
                change({ anchorDate: event.target.value });
              }}
            />
          </FormRow>

          <FormRow label="Time zone">
            <TimeZonePicker
              value={draft.timeZone}
              teamZones={teamZones}
              now={now}
              onChange={(timeZone) => {
                change({ timeZone });
              }}
            />
          </FormRow>

          <div className="mt-auto flex flex-wrap items-center gap-2.5 pt-1">
            <Button
              type="button"
              size="sm"
              className={cn(PROJECT_PRIMARY_BUTTON, "h-[30px]")}
              disabled={!complete || save.isPending}
              onClick={() => {
                submit({
                  weekday: draft.weekday,
                  everyNWeeks: draft.everyNWeeks,
                  anchorDate: draft.anchorDate,
                  timeZone: draft.timeZone,
                  startTime: draft.startTime,
                });
              }}
              data-testid="save-schedule"
            >
              <Check aria-hidden="true" />
              {save.isPending ? "Saving…" : "Save schedule"}
            </Button>
            <button
              type="button"
              className={PROJECT_GHOST_ACTION}
              disabled={!dirty || save.isPending}
              onClick={() => {
                setDraft(saved);
                setFailure(null);
              }}
            >
              Cancel
            </button>
            {pattern && (
              <button
                type="button"
                className={PROJECT_GHOST_ACTION}
                disabled={save.isPending}
                onClick={() => {
                  submit(null);
                }}
                data-testid="stop-repeating"
              >
                Stop repeating
              </button>
            )}
            <span className="flex-1" />
            <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)} data-testid="detected-zone">
              Detected from this computer: {zoneName(detected) ?? zoneLabel(detected)}
            </span>
          </div>
        </section>

        <div className="flex w-[330px] flex-none flex-col gap-3">
          <NextMeetingsPanel draft={draft} viewerZone={viewerZone} teamZones={teamZones} now={now} />
          <section className="glass-card flex flex-col gap-1.5 rounded-[10px] px-[18px] py-3.5" data-testid="when-to-prepare">
            <h3 className={cn("flex items-center gap-2 font-semibold", TOOLBOX_TEXT.content)}>
              <Lightbulb className="h-[15px] w-[15px] text-[#FBBF24]" aria-hidden="true" />
              When to prepare
            </h3>
            <p className={cn(TOOLBOX_TEXT.meta, "leading-relaxed text-muted-foreground")}>
              Ideally the business day before the meeting. Nothing forces it: earlier or the same morning is fine.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] items-center gap-3.5">
      {htmlFor ? (
        <label htmlFor={htmlFor} className={cn(TOOLBOX_TEXT.content, "font-medium text-muted-foreground")}>
          {label}
        </label>
      ) : (
        <span className={cn(TOOLBOX_TEXT.content, "font-medium text-muted-foreground")}>{label}</span>
      )}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Next meetings (frame F1's right column)
// ---------------------------------------------------------------------------

/** One upcoming meeting as the panel shows it. */
export interface NextMeetingRow {
  /** `YYYY-MM-DD` in the pattern's zone. */
  date: string;
  /** The tile: day of the month and short month ("26", "Oct"). */
  day: string;
  month: string;
  /** "Monday, October 26 · 10:00 AM" in the viewer's zone; the date alone without a start time. */
  when: string;
  /** "Prepare briefs by Friday, Oct 23" (first) / "Prepare by Friday, Nov 6" (others). */
  prepare: string;
  /** The meeting's start, or null without a start time. */
  start: Date | null;
}

function zonedParts(at: Date, timeZone: string): { long: string; day: string; month: string } {
  const long = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone }).format(at);
  const parts = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone }).formatToParts(at);
  return {
    long,
    day: parts.find((part) => part.type === "day")?.value ?? "",
    month: parts.find((part) => part.type === "month")?.value ?? "",
  };
}

/**
 * The next `count` meetings of a draft: dates from `upcomingMeetingDates`,
 * the hour from `zonedWallTimeToInstant` shown in `viewerZone`. Empty when
 * the draft has no valid first date or zone yet. Pure; exported for tests.
 */
export function nextMeetingRows(draft: MeetingScheduleDraft, viewerZone: string, now: Date = new Date(), count = 3): NextMeetingRow[] {
  if (!DATE_PATTERN.test(draft.anchorDate) || !isKnownTimeZone(draft.timeZone)) return [];
  let dates: string[];
  try {
    dates = upcomingMeetingDates(draft, count, now);
  } catch {
    return [];
  }
  const hasTime = MEETING_START_TIME_PATTERN.test(draft.startTime);
  return dates.map((date, index) => {
    const prepareLabel = index === 0 ? "Prepare briefs by" : "Prepare by";
    const prepare = `${prepareLabel} ${formatMeetingDate(prepareByDate(date))}`;
    if (hasTime) {
      const start = zonedWallTimeToInstant(date, draft.startTime, draft.timeZone);
      const parts = zonedParts(start, viewerZone);
      // The row shows the viewer's own day, so "prepare by" counts back from that day.
      const viewerPrepare = `${prepareLabel} ${formatMeetingDate(prepareByDate(localDateIn(viewerZone, start)))}`;
      const time = formatLocalClockParts(start.toISOString(), { timeZone: viewerZone, now })?.time ?? "";
      return { date, day: parts.day, month: parts.month, when: time ? `${parts.long} · ${time}` : parts.long, prepare: viewerPrepare, start };
    }
    const local = dateParts(date);
    return {
      date,
      day: String(local.getDate()),
      month: local.toLocaleDateString("en-US", { month: "short" }),
      when: local.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
      prepare,
      start: null,
    };
  });
}

/**
 * The footer's ": 8:30 PM in Kolkata": the first meeting's hour in the first
 * team zone other than the pattern's, with that zone's weekday when the hour
 * falls on another day there (": Tuesday 7:30 AM in Kolkata"). Empty without
 * a start time or such a zone.
 */
export function otherZoneSuffix(rows: readonly NextMeetingRow[], patternZone: string, teamZones: readonly string[], now: Date = new Date()): string {
  const start = rows[0]?.start;
  if (!start) return "";
  const other = teamZones.find(
    (zone) => currentZoneId(zone) !== currentZoneId(patternZone) && isKnownTimeZone(zone)
  );
  if (!other) return "";
  const time = formatLocalClockParts(start.toISOString(), { timeZone: other, now })?.time;
  if (!time) return "";
  const city = zoneCity(currentZoneId(other));
  if (localDateIn(other, start) === rows[0]?.date) return `: ${time} in ${city}`;
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: other }).format(start);
  return `: ${weekday} ${time} in ${city}`;
}

function NextMeetingsPanel({
  draft,
  viewerZone,
  teamZones,
  now,
}: {
  draft: MeetingScheduleDraft;
  viewerZone: string;
  teamZones: readonly string[];
  now: Date | undefined;
}) {
  const at = now ?? new Date();
  const rows = nextMeetingRows(draft, viewerZone, at);
  const hasTime = MEETING_START_TIME_PATTERN.test(draft.startTime);
  const suffix = otherZoneSuffix(rows, draft.timeZone, teamZones, at);

  return (
    <section
      className="glass-card flex flex-col gap-2.5 rounded-[10px] px-[18px] py-4"
      aria-label="Next meetings"
      data-testid="next-meetings"
    >
      <h3 className={TOOLBOX_TEXT.eyebrow}>Next meetings</h3>
      {rows.length === 0 ? (
        <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="next-meetings-empty">
          Pick the first meeting and the time zone to see the next meetings.
        </p>
      ) : (
        <ol className="flex flex-col gap-2.5">
          {rows.map((row) => (
            <li key={row.date} className="flex items-center gap-3" data-testid="next-meeting">
              <span
                className="flex h-[42px] w-[42px] flex-none flex-col items-center justify-center rounded-[9px] border border-[#27272A] bg-[#1A1A1E] text-[11px] leading-tight text-muted-foreground"
                aria-hidden="true"
              >
                <b className="font-display text-base font-bold text-foreground">{row.day}</b>
                {row.month}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className={cn(TOOLBOX_TEXT.content, "font-semibold")} data-testid="next-meeting-when">
                  {row.when}
                </span>
                <span className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")} data-testid="next-meeting-prepare">
                  {row.prepare}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {!hasTime && (
        <p className={cn("flex items-center gap-1.5 text-[#FBBF24]", TOOLBOX_TEXT.meta)} data-testid="add-start-time">
          <AlertTriangle className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          Add a start time so everyone sees the hour.
        </p>
      )}
      <p className={cn(TOOLBOX_TEXT.meta, "leading-relaxed text-muted-foreground")} data-testid="next-meetings-footer">
        Updates as you pick. Shown in each person's own time zone on their screen{suffix}.
      </p>
    </section>
  );
}
